import { useEffect, useMemo, useState } from 'react';
import { Text, Title } from '@tremor/react';
import { fetchSales } from '../api/drilldown';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { SalesTicket } from '../api/types';
import SalesAggregationView from './SalesAggregationView';
import LinePartsDrilldownModal from './LinePartsDrilldownModal';
import IconButton from './IconButton';
import Modal from './Modal';
import { CloseIcon } from './icons';

interface Props {
  /** "00".."23" */
  hour: string;
  /** YYYY-MM-DD; defaults to today on the daemon side. */
  date?: string;
  onClose: () => void;
}

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

function hourOf(secs: number): number {
  return Math.floor(secs / 3600);
}

export default function HourDrilldownModal({ hour, date, onClose }: Props) {
  const [data, setData] = useState<SalesTicket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drilledLine, setDrilledLine] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const day = date ?? localYMD();
    fetchSales({ from: day, to: day, type_filter: 'all', paid_filter: 'all' })
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [date]);

  const hourNum = parseInt(hour, 10);
  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((r) => hourOf(r.itime_secs) === hourNum);
  }, [data, hourNum]);

  const totals = useMemo(() => {
    let revenue = 0;
    for (const r of filtered) revenue += r.total_mxn;
    return { count: filtered.length, revenue };
  }, [filtered]);

  return (
    <>
    <Modal
      onClose={onClose}
      ariaLabel={`${t.hourDrill.title} — ${date ?? localYMD()} ${t.hourDrill.hourPrefix} ${hour}:00–${String((parseInt(hour, 10) + 1) % 24).padStart(2, '0')}:00`}
      role="dialog"
      panelClassName="max-w-6xl my-8 p-6"
    >
      <div className="flex items-start gap-4">
        <Title>
          {t.hourDrill.title} — {date ?? localYMD()} {t.hourDrill.hourPrefix} {hour}:00–{String((parseInt(hour, 10) + 1) % 24).padStart(2, '0')}:00
          <span className="text-tremor-content text-base font-normal ml-2">
            · {totals.count} {t.hourDrill.summary} · {fmtMxn.format(totals.revenue)}
          </span>
        </Title>
        <IconButton label={t.common.close} onClick={onClose} className="ml-auto">
          <CloseIcon />
        </IconButton>
      </div>

      {error && (
        <div className="rounded-tremor-default bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200 p-4 mt-4">
          {error.includes('503') ? t.errors.daemonOffline : error}
        </div>
      )}
      {!error && (
        <SalesAggregationView
          tickets={filtered}
          loading={!data}
          onLineClick={setDrilledLine}
        />
      )}
      {data && filtered.length === 0 && !error && (
        <Text className="mt-2 text-tremor-content text-sm">{t.chartToggle.empty}</Text>
      )}
    </Modal>
    {drilledLine && (
      <LinePartsDrilldownModal
        tickets={filtered}
        line={drilledLine}
        onClose={() => setDrilledLine(null)}
      />
    )}
    </>
  );
}
